import { IUserRepository } from './IUserRepository';
import type BaseApiProvider from 'providers/BaseApiProvider';
import { injectable, inject } from 'inversify';
import { ApiResponseModel } from 'models/ApiResponseModel';
import UserLoginModel from 'models/auth/UserLoginModel';
import Logger from 'utilities/log';
import { AuthRoleModel, AuthUserGetPaload, AuthUserPayloadModel, AuthUserSimpleModel
    , AuthLegalUserGetPaload, UserLegalFilterPayload, AuthLegalUserSimpleModel
    , AuthRealUserGetPaload, UserRealFilterPayload, AuthRealUserSimpleModel
    , AuthUserAddressGetPaload, UserAddressFilterPayload, AuthUserAddressSimpleModel
    , AuthUserStatusGetPaload, UserStatusFilterPayload, AuthUserStatusSimpleModel,
    AuthLegalUserPayloadModel,
    AuthRealUserPayloadModel,
    AuthUserAddressPayloadModel,
    AuthUserStatusPayloadModel,
    AuthGeneralUserGetPaload,
    AuthGeneralUserSimpleModel
 } from 'models/auth/AuthUserModels';
import {PaginationModel} from 'models/PaginationModel';
import { AuthRoleGetPaload, AuthRoleSimpleModel,AuthRolePayloadModel} from 'models/auth/RoleModels';

@injectable()
class UserRepository implements IUserRepository {

    @inject(Symbol.for("BaseApiProvider_5080")) private _provider?: BaseApiProvider;


    public async GetAllUsersList(params: AuthUserGetPaload): Promise<{
        rows: AuthUserSimpleModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Users/Info/GetAllUsersList`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get accounts list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => AuthUserSimpleModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get accounts list: ${error}`);
            throw new Error(`Failed to get accounts list: ${error}`);
        }
    }


    public async GetAllUserRolesList(params: AuthUserGetPaload): Promise<{
        rows: AuthRoleModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Users/Info/GetAllUserRolesList`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get accounts list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => AuthRoleModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get accounts list: ${error}`);
            throw new Error(`Failed to get accounts list: ${error}`);
        }
    }

    public async GetCurrentUserRole(): Promise<string> {
        try {
            const url = `Api/UserInfo/GetCurrentUserRole`;
            const response = await this._provider!.get(url);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get accounts list');
                return "";
            }
            return response["data"]["title"];
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to delete item request detail: ${error}`);
            throw new Error(`Failed to delete item request detail: ${error}`);
        }
    }

    public async SetUserInfo(params: AuthUserPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `User/Action/SetUserInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }

    public async SetUserRoleInfo(params: AuthRoleModel): Promise<ApiResponseModel | null> {
        try {
            const url = `User/Action/SetUserRoleInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }

    public async DeleteUserRole(Id: number): Promise<ApiResponseModel> {
        try {
            const url = `User/Action/DeleteUserRole`;
            const formData = new FormData();
            formData.append('Id', Id.toString());
            const response = await this._provider!.postForm(url, formData);
            return ApiResponseModel.fromJson(response);
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to delete item request detail: ${error}`);
            throw new Error(`Failed to delete item request detail: ${error}`);
        }
    }


    public async SetGeneralUserInfo(params: AuthUserPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/UserAction/SaveGeneralUserInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }
   
    public async SetRadAccountIdOnUser(RadAccountId: number,UserId: number): Promise<ApiResponseModel> {
        try {
            const url = `User/Action/DeleteUserRole`;
            const formData = new FormData();
            formData.append('RadAccountId', RadAccountId.toString());
            formData.append('UserId', UserId.toString());
            const response = await this._provider!.postForm(url, formData);
            return ApiResponseModel.fromJson(response);
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set item request detail: ${error}`);
            throw new Error(`Failed to set item request detail: ${error}`);
        }
    }
    
    public async SaveLegalUserDetailInfo(params: AuthLegalUserPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/UserAction/SaveLegalUserDetailInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }
    public async SaveRealUserDetailInfo(params: AuthRealUserPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/UserAction/SaveRealUserDetailInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }

    public async AddRealUserLogo(userId: number,type: number, File1: File): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/UserAction/AddRealUserLogo`;
                const formData = new FormData();
                formData.append('userId', userId.toString());
                formData.append('type', type.toString());
                formData.append('File1', File1);
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to add account logo: ${error}`);
                throw new Error(`Failed to add account logo: ${error}`);
            }
        }
    public async SaveUserAddressInfo(params: AuthUserAddressPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/UserAction/SaveUserAddressInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }
    public async SaveUserStatusInfo(params: AuthUserStatusPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/UserAction/SaveUserStatusLogInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }
//#region  UsersGeneral
public async GetAllUsersGeneralInfo(params: AuthGeneralUserGetPaload): Promise<{
    rows: AuthGeneralUserSimpleModel[];
    pagination: PaginationModel;
} | null> {
    try {
        const url = `Api/UserInfo/GetAllUsersGeneralInfo`;
        const response = await this._provider!.post(url, params);
        const res = ApiResponseModel.fromJson(response);

        if (!res.status) {
            Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get users list');
            return null;
        }

        return {
            rows: response["data"]["rows"].map((js: any) => AuthGeneralUserSimpleModel.fromJson(js)),
            pagination: PaginationModel.fromJson(response["data"])
        };
    } catch (error) {
        Logger.event('API_RESPONSE_ERROR', `Failed to get users list: ${error}`);
        throw new Error(`Failed to get users list: ${error}`);
    }
}
public async GetUserGeneralInfo(params: AuthGeneralUserGetPaload): Promise<AuthGeneralUserSimpleModel| null> {
    try {
        const url = `Api/UserInfo/GetUserGeneralInfo`;
        const response = await this._provider!.post(url, params);
        const res = ApiResponseModel.fromJson(response);

        if (!res.status) {
            Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get user info');
            return null;
        }
        return AuthGeneralUserSimpleModel.fromJson(response["data"]);

    } catch (error) {
         if (error instanceof Error) {
            Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
            throw new Error(`Api failed: ${error.message}`);
        } else {
            Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
            throw new Error('Api failed: Unknown error');
        }
    }
}

//#endregion UsersGeneral
//#region  LegalUser
    public async GetAllLegalUserInfo(params: AuthLegalUserGetPaload): Promise<{
        rows: AuthLegalUserSimpleModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/UserInfo/GetAllLegalUserInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get users list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => AuthLegalUserSimpleModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get users list: ${error}`);
            throw new Error(`Failed to get users list: ${error}`);
        }
    }
    public async GetLegalUserInfo(params: AuthLegalUserGetPaload): Promise<AuthLegalUserSimpleModel| null> {
        try {
            const url = `Api/UserInfo/GetLegalUserInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get user info');
                return null;
            }
            return AuthLegalUserSimpleModel.fromJson(response["data"]);

        } catch (error) {
             if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }

//#endregion LegalUser

//#region  RealUser
public async GetAllRealUserInfo(params: AuthRealUserGetPaload): Promise<{
    rows: AuthRealUserSimpleModel[];
    pagination: PaginationModel;
} | null> {
    try {
        const url = `Api/UserInfo/GetAllRealUserInfo`;
        const response = await this._provider!.post(url, params);
        const res = ApiResponseModel.fromJson(response);

        if (!res.status) {
            Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get users list');
            return null;
        }

        return {
            rows: response["data"]["rows"].map((js: any) => AuthRealUserSimpleModel.fromJson(js)),
            pagination: PaginationModel.fromJson(response["data"])
        };
    } catch (error) {
        Logger.event('API_RESPONSE_ERROR', `Failed to get users list: ${error}`);
        throw new Error(`Failed to get users list: ${error}`);
    }
}
public async GetRealUserInfo(params: AuthRealUserGetPaload): Promise<AuthRealUserSimpleModel| null> {
    try {
        const url = `Api/UserInfo/GetRealUserInfo`;
        const response = await this._provider!.post(url, params);
        const res = ApiResponseModel.fromJson(response);

        if (!res.status) {
            Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get user info');
            return null;
        }
        return AuthRealUserSimpleModel.fromJson(response["data"]);

    } catch (error) {
         if (error instanceof Error) {
            Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
            throw new Error(`Api failed: ${error.message}`);
        } else {
            Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
            throw new Error('Api failed: Unknown error');
        }
    }
}

//#endregion RealUser

//#region  UserAddress
public async GetAllUserAddressInfo(params: AuthUserAddressGetPaload): Promise<{
    rows: AuthUserAddressSimpleModel[];
    pagination: PaginationModel;
} | null> {
    try {
        const url = `Api/UserInfo/GetAllUserAddressInfo`;
        const response = await this._provider!.post(url, params);
        const res = ApiResponseModel.fromJson(response);

        if (!res.status) {
            Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get users address list');
            return null;
        }

        return {
            rows: response["data"]["rows"].map((js: any) => AuthUserAddressSimpleModel.fromJson(js)),
            pagination: PaginationModel.fromJson(response["data"])
        };
    } catch (error) {
        Logger.event('API_RESPONSE_ERROR', `Failed to get users list: ${error}`);
        throw new Error(`Failed to get users list: ${error}`);
    }
}
public async GetUserAddressInfo(params: AuthUserAddressGetPaload): Promise<AuthUserAddressSimpleModel| null> {
    try {
        const url = `Api/UserInfo/GetUserAddressInfo`;
        const response = await this._provider!.post(url, params);
        const res = ApiResponseModel.fromJson(response);

        if (!res.status) {
            Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get user address info');
            return null;
        }
        return AuthUserAddressSimpleModel.fromJson(response["data"]);

    } catch (error) {
         if (error instanceof Error) {
            Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
            throw new Error(`Api failed: ${error.message}`);
        } else {
            Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
            throw new Error('Api failed: Unknown error');
        }
    }
}

//#endregion UserAddress
//#region  UserStatus
    public async GetAllUserStatusLogInfo(params: AuthUserStatusGetPaload): Promise<{
        rows: AuthUserStatusSimpleModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/UserInfo/GetAllUserStatusLogInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get users address list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => AuthUserStatusSimpleModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get users list: ${error}`);
            throw new Error(`Failed to get users list: ${error}`);
        }
    }
    public async GetUserStatusLogInfo(params: AuthUserStatusGetPaload): Promise<AuthUserStatusSimpleModel| null> {
        try {
            const url = `Api/UserInfo/GetUserStatusLogInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get user address info');
                return null;
            }
            return AuthUserStatusSimpleModel.fromJson(response["data"]);

        } catch (error) {
             if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }

//#endregion UserStatus

    public async AdminLogin(UserName: string, PassWord: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/AdminLogin`;
            const formData = new FormData();
            formData.append('UserName', UserName);
            formData.append('PassWord', PassWord);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Login failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Login failed: Unknown error');
                throw new Error('Login failed: Unknown error');
            }
        }
    }

    public async Login(UserName: string, PassWord: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/Login`;
            const formData = new FormData();
            formData.append('UserName', UserName);
            formData.append('PassWord', PassWord);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Login failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Login failed: Unknown error');
                throw new Error('Login failed: Unknown error');
            }
        }
    }
    
    public async StartRegister(isLegal: boolean,MobileNo: string, NationalCode: string, mainAgentNationalCode: string): Promise<UserLoginModel | null> {
        
       try {
            const url = `Api/UserAction/StartRegister`;
            const formData = new FormData();
            formData.append('MobileNo', MobileNo);
            formData.append('NationalCode', NationalCode);
            formData.append('mainAgentNationalCode', mainAgentNationalCode);
            formData.append('isLegal', isLegal.toString());
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `StartRegister failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'StartRegister failed: Unknown error');
                throw new Error('StartRegister failed: Unknown error');
            }
        }
}

    public async GetLoginCode(MobileNo: string, NationalCode: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/GetLoginCode`;
            const formData = new FormData();
            formData.append('MobileNo', MobileNo);
            formData.append('NationalCode', NationalCode);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `GetLoginCode failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'GetLoginCode failed: Unknown error');
                throw new Error('GetLoginCode failed: Unknown error');
            }
        }
    }

    public async LoginWithCode(MobileNo: string, NationalCode: string, ConfirmCode: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/LoginWithCode`;
            const formData = new FormData();
            formData.append('MobileNo', MobileNo);
            formData.append('ConfirmCode', ConfirmCode);
            formData.append('NationalCode', NationalCode);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Login failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Login failed: Unknown error');
                throw new Error('Login failed: Unknown error');
            }
        }
    }

    public async ForgetPassword(MobileNo: string,NationalCode: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/ForgetPassword`;
            const formData = new FormData();
            formData.append('MobileNo', MobileNo);
            formData.append('NationalCode', NationalCode);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `GetLoginCode failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'GetLoginCode failed: Unknown error');
                throw new Error('GetLoginCode failed: Unknown error');
            }
        }
    }

    public async ForgetPasswordCheckCode(MobileNo: string, NationalCode: string, ConfirmCode: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/ForgetPasswordCheckCode`;
            const formData = new FormData();
            formData.append('MobileNo', MobileNo);
            formData.append('ConfirmCode', ConfirmCode);
            formData.append('NationalCode', NationalCode);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Login failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Login failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Login failed: Unknown error');
                throw new Error('Login failed: Unknown error');
            }
        }
    }

    public async SetNewPassword(Password: string, RepeatPassword: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/SetNewPassword`;
            const formData = new FormData();
            formData.append('Password', Password);
            formData.append('RepeatPassword', RepeatPassword);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'SetNewPassword failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `SetNewPassword failed: ${error.message}`);
                throw new Error(`SetNewPassword failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'SetNewPassword failed: Unknown error');
                throw new Error('SetNewPassword failed: Unknown error');
            }
        }
    }

    public async ChangePassword(OldPassword: string,Password: string, RepeatPassword: string): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/UserAction/ChangePassword`;
            const formData = new FormData();
            formData.append('OldPassword', OldPassword);
            formData.append('Password', Password);
            formData.append('RepeatPassword', RepeatPassword);
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set account info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }
    
    public async GenerateAppActivationCode(mobile: string): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserAction/Login`;
            const rowData = { mobile: mobile };
            const response = await this._provider!.post(url, rowData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to generate activation code');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]["userData"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to generate activation code: ${error.message}`);
                throw new Error(`Failed to generate activation code: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Failed to generate activation code: Unknown error');
                throw new Error('Failed to generate activation code: Unknown error');
            }
        }
    }
    
    public async GetCurrentUserInfo(): Promise<UserLoginModel | null> {
        try {
            const url = `Api/UserInfo/GetCurrentUserInfo`;
            const response = await this._provider!.postForm(url, null);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'GetCurrentUserInfo failed');
                return null;
            }
            return UserLoginModel.fromJson(response["data"]);
        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `GetCurrentUserInfo failed: ${error.message}`);
                throw new Error(`Login failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'GetCurrentUserInfo failed: Unknown error');
                throw new Error('GetCurrentUserInfo failed: Unknown error');
            }
        }
    }

    //#region  Role
    public async GetAllRoleInfo(params: AuthRoleGetPaload): Promise<{
        rows: AuthRoleSimpleModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/RoleInfo/GetAllRoleInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
    
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get users list');
                return null;
            }
    
            return {
                rows: response["data"]["rows"].map((js: any) => AuthRoleSimpleModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get users list: ${error}`);
            throw new Error(`Failed to get users list: ${error}`);
        }
    }
    public async GetRoleInfo(params: AuthRoleGetPaload): Promise<AuthRoleSimpleModel| null> {
        try {
            const url = `Api/RoleInfo/GetRoleInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
    
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get user info');
                return null;
            }
            return AuthRoleSimpleModel.fromJson(response["data"]);
    
        } catch (error) {
             if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    public async SaveUserRoleInfo(params: AuthRolePayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/RoleAction/SaveUserRoleInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to set role info: ${error}`);
            throw new Error(`Failed to set account info: ${error}`);
        }
    }

    public async DeleteRoleInfo(Id: number): Promise<ApiResponseModel> {
        try {
            const url = `Api/RoleAction/DeleteRoleInfo`;
            const formData = new FormData();
            formData.append('Id', Id.toString());
            const response = await this._provider!.postForm(url, formData);
            return ApiResponseModel.fromJson(response);
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to delete item request detail: ${error}`);
            throw new Error(`Failed to delete item request detail: ${error}`);
        }
    }

    //#endregion Role
}

export default UserRepository;
